﻿import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Checkbox, NumericTextBox, RadioButton, Slider } from '@progress/kendo-react-inputs';
import React from 'react';
import SettingsSubSection from '../../../../../components/common/SettingsSubSection';
import { UnitFormatter } from '../../../../../formatters';
import useFilterGroup from '../../../../../hooks/use-filter-group';
import { FilterGroupType } from '../../../../../types/filter-group';
import { ColorPickerWithDefault } from '../../../../components/inputs/ColorPickerWithDefault';
import { SliderWithTooltip } from '../../../../components/inputs/SliderWithTooltip';
import { useAppDispatch } from '../../../../hooks/store-hooks';
import { patchFocusedWorkspaceView } from '../../../../state/store';
import useView from '../../hooks/use-view';
import {
    useViewCheckboxValue,
    useViewColorValue,
    useViewDropdownValue,
    useViewNumericValue,
    useViewSliderValue
} from '../../hooks/view-forms';
import { DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';

const DEPARTURE_AIRPORT_DEFAULT_COLOR = '#3030f0';
const ARRIVAL_AIRPORT_DEFAULT_COLOR = '#e100ff';

const multipleRingsData = [
    { text: '1', value: 1 },
    { text: '2', value: 2 },
    { text: '3', value: 3 },
    { text: '4', value: 4 },
    { text: '5', value: 5 }
];

const airportLabelTypeOptions = [
    { text: 'FAA or ICAO', value: 'faa' },
    { text: 'IATA', value: 'iata' },
    { text: 'ICAO', value: 'icao' },
    { text: 'IATA/ICAO', value: 'iataIcao' },
    { text: 'ICAO/IATA', value: 'icaoIata' }
];

const rangeRingsLinesStyleOptions = [
    { text: 'Solid', value: 'solid' },
    { text: 'None', value: 'none' }
];

const rangeRingsFillStyleOptions = [
    { text: 'Solid', value: 'solid' },
    { text: 'None', value: 'none' }
];

export function AirportLayerSettings() {
    const fontSize = useViewSliderValue((x) => x.staticData.airport.settings.fontSize);
    const activeRunwayDepiction = useViewCheckboxValue((x) => x.staticData.airport.settings.activeRunwayDepiction);
    const departureColor = useViewColorValue((x) => x.staticData.airport.settings.departureColor);
    const arrivalColor = useViewColorValue((x) => x.staticData.airport.settings.arrivalColor);
    const directionIndicator = useViewCheckboxValue((x) => x.staticData.airport.settings.directionIndicator);
    const runwayLabels = useViewCheckboxValue((x) => x.staticData.airport.settings.runwayLabels);
    const runwayWindIndicators = useViewCheckboxValue((x) => x.staticData.airport.settings.runwayWindIndicators);
    const runwayWindGust = useViewCheckboxValue((x) => x.staticData.airport.settings.runwayWindGust);
    const runwayRvrIndicators = useViewCheckboxValue((x) => x.staticData.airport.settings.runwayRvrIndicators);
    const rangeRingsEnabled = useViewCheckboxValue((x) => x.staticData.airport.settings.rangeRingsEnabled);
    const rangeRingsLineColor = useViewColorValue((x) => x.staticData.airport.settings.rangeRingsLineColor);
    const rangeRingsFillColor = useViewColorValue((x) => x.staticData.airport.settings.rangeRingsFillColor);
    const rangeRingsOpacity = useViewSliderValue((x) => x.staticData.airport.settings.rangeRingsOpacity);
    const rangeRingsSingleRing = useViewCheckboxValue((x) => x.staticData.airport.settings.rangeRingsSingleRing);
    const rangeRingsSingleRingDistance = useViewNumericValue((x) => x.staticData.airport.settings.rangeRingsSingleRingDistance);
    const rangeRingsMultipleRingSpacing = useViewNumericValue((x) => x.staticData.airport.settings.rangeRingsMultipleRingSpacing);
    const airportLabelType = useViewDropdownValue(airportLabelTypeOptions, (x) => x.staticData.airport.settings.airportLabelType);
    const rangeRingsLineStyle = useViewDropdownValue(rangeRingsLinesStyleOptions, (x) => x.staticData.airport.settings.rangeRingsLineStyle);
    const rangeRingsFillStyle = useViewDropdownValue(rangeRingsFillStyleOptions, (x) => x.staticData.airport.settings.rangeRingsFillStyle);
    const rangeRingsMultipleRingsDropdown = useViewDropdownValue(
        multipleRingsData,
        (x) => x.staticData.airport.settings.rangeRingsMultipleRings
    );
    const applyToAllAirports = useViewCheckboxValue((x) => x.staticData.airport.settings.applyToAllAirports);

    const { view } = useView();
    const dispatch = useAppDispatch();

    const gustFactor = view.staticData.airport.settings.runwayWindGustFactor ?? 0.5;
    const handleGustFactorChange = React.useCallback(
        (factor: number) => {
            dispatch(
                patchFocusedWorkspaceView((v) => {
                    v.staticData.airport.settings.runwayWindGustFactor = factor;
                })
            );
        },
        [dispatch]
    );
    const filterGroups = useFilterGroup(FilterGroupType.airport);
    const selectedGroupId = view.staticData.airport.settings.selectedGroupId ?? null;
    const selectedFilterGroup = React.useMemo(() => filterGroups.find((x) => x.id === selectedGroupId), [filterGroups, selectedGroupId]);

    const handleFilterGroupChanged = React.useCallback(
        (e: DropDownListChangeEvent) => {
            dispatch(
                patchFocusedWorkspaceView((v) => {
                    v.staticData.airport.settings.selectedGroupId = e.value.id;
                })
            );
        },
        [view, dispatch]
    );

    return (
        <div className="d-flex flex-column gap-2 px-3 py-2 overflow-auto" style={{ width: '320px' }}>
            <SettingsSubSection title="Airport details">
                <div>
                    <div>Font size: {fontSize.value}</div>
                    <Slider
                        min={4}
                        max={20}
                        step={1}
                        largeStep={1}
                        className="w-100"
                        value={fontSize.value}
                        onChange={(e) => fontSize.onChange?.({ ...e, value: Math.round(e.value) })}
                    />
                </div>

                <div className="d-flex align-items-center gap-2">
                    <div>Label type</div>
                    <DropdownListWithValueField {...airportLabelType} className={'w-50'}></DropdownListWithValueField>
                </div>
                <Checkbox {...runwayLabels} className="flex-grow-1" label={'Runway labels'}></Checkbox>
            </SettingsSubSection>

            <SettingsSubSection title="Real time airport status">
                <Checkbox {...activeRunwayDepiction} className="flex-grow-1" label={'Active runway depiction'}></Checkbox>
                <div className="d-flex align-items-center gap-2">
                    <div>Departure color</div>
                    <ColorPickerWithDefault
                        defaultColor={DEPARTURE_AIRPORT_DEFAULT_COLOR}
                        views={['palette', 'gradient']}
                        {...departureColor}
                    />
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div>Arrival color</div>
                    <ColorPickerWithDefault
                        defaultColor={ARRIVAL_AIRPORT_DEFAULT_COLOR}
                        views={['palette', 'gradient']}
                        {...arrivalColor}
                    />
                </div>
                <Checkbox {...directionIndicator} className="flex-grow-1" label={'Direction indicator'}></Checkbox>
                <Checkbox {...runwayWindIndicators} className="flex-grow-1" label={'Wind indicator'}></Checkbox>
                <Checkbox {...runwayRvrIndicators} className="flex-grow-1" label={'RVR indicator'}></Checkbox>
                <Checkbox {...runwayWindGust} className="flex-grow-1" label={'Include gusts in wind indicator'}></Checkbox>
                {runwayWindGust.value && (
                    <div className="d-flex align-items-center gap-3 ms-4">
                        <div>Gust blend</div>
                        <div className="d-flex align-items-center gap-2">
                            <RadioButton
                                name="gustFactor"
                                value="half"
                                checked={gustFactor === 0.5}
                                onChange={() => handleGustFactorChange(0.5)}
                            />
                            <span>50%</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <RadioButton
                                name="gustFactor"
                                value="full"
                                checked={gustFactor === 1.0}
                                onChange={() => handleGustFactorChange(1.0)}
                            />
                            <span>100%</span>
                        </div>
                    </div>
                )}
            </SettingsSubSection>

            <SettingsSubSection title="Range rings">
                <Checkbox {...rangeRingsEnabled} className="flex-grow-1" label={'Enable range rings'}></Checkbox>
                <div className="d-flex align-items-center gap-2">
                    <ColorPickerWithDefault defaultColor="rgb(0, 0, 0)" views={['palette', 'gradient']} {...rangeRingsLineColor} />
                    <div style={{ width: '3ch' }}>Line</div>
                    <DropdownListWithValueField {...rangeRingsLineStyle} className={'w-50'}></DropdownListWithValueField>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <ColorPickerWithDefault defaultColor="rgb(139, 193, 201)" views={['palette', 'gradient']} {...rangeRingsFillColor} />
                    <div style={{ width: '3ch' }}>Fill</div>
                    <DropdownListWithValueField {...rangeRingsFillStyle} className={'w-50'}></DropdownListWithValueField>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div className="flex-shrink-0">Opacity</div>
                    <SliderWithTooltip min={0} max={1} {...rangeRingsOpacity} className="flex-grow-1" />
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div>
                        <RadioButton
                            name="ringType"
                            value="single"
                            checked={rangeRingsSingleRing.value as boolean}
                            onChange={(e) => rangeRingsSingleRing?.onChange?.({ ...e, value: true })}
                        />
                    </div>
                    <div>Single ring at</div>
                    <NumericTextBox
                        className={'w-50'}
                        format={`0 ${UnitFormatter.distanceUnit}`}
                        min={0}
                        max={1000}
                        {...rangeRingsSingleRingDistance}
                    />
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div>
                        <RadioButton
                            name="ringType"
                            value="multiple"
                            checked={!rangeRingsSingleRing.value}
                            onChange={(e) => rangeRingsSingleRing?.onChange?.({ ...e, value: false })}
                        />
                    </div>
                    <DropDownList {...rangeRingsMultipleRingsDropdown} style={{ width: '8ch' }}></DropDownList>
                    <div>rings at</div>
                    <NumericTextBox
                        className={'w-50'}
                        format={`0 ${UnitFormatter.distanceUnit}`}
                        min={5}
                        max={50}
                        {...rangeRingsMultipleRingSpacing}
                    />
                </div>
            </SettingsSubSection>
            <SettingsSubSection title="Range ring filter">
                <div className="d-flex align-items-center gap-2">
                    <div>
                        <RadioButton
                            name="applyTo"
                            value="all"
                            checked={applyToAllAirports.value as boolean}
                            onChange={(e) => applyToAllAirports?.onChange?.({ ...e, value: true })}
                        />
                    </div>
                    <div>All selected airports</div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div>
                        <RadioButton
                            name="applyTo"
                            value="selected"
                            checked={!applyToAllAirports.value}
                            onChange={(e) => applyToAllAirports?.onChange?.({ ...e, value: false })}
                        />
                    </div>
                    <DropDownList
                        data={filterGroups}
                        value={selectedFilterGroup}
                        textField="name"
                        dataItemKey="id"
                        onChange={handleFilterGroupChanged}
                        className={'w-100'}
                        disabled={applyToAllAirports.value as boolean}
                    />
                </div>
            </SettingsSubSection>
        </div>
    );
}
